import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from './firebaseConfig';

/**
 * Module-level cache so multiple modals don't re-fetch.
 */
let cache = null;
let inflight = null;

async function fetchProjectManagers() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const q = query(
        collection(firestore, 'user_roles'),
        where('role', '==', 'project_manager')
      );
      const snap = await getDocs(q);
      cache = snap.docs
        .map((d) => ({
          uid: d.id,
          displayName: d.data().displayName || '',
          email: d.data().email || '',
        }))
        .filter((pm) => pm.displayName)
        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'pl'));
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Hook: returns the list of active project managers.
 */
export default function useProjectManagers() {
  const [managers, setManagers] = useState(cache || []);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cache) return undefined;
    let mounted = true;
    fetchProjectManagers()
      .then((list) => {
        if (!mounted) return;
        setManagers(list);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        console.error('useProjectManagers: failed to load', err);
        setError(err);
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return { managers, loading, error };
}
